import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";
import { useGenerateLedgerReport } from "http-hooks/salebill/useSalebill";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import useAuth from "hooks/useAuth";

const agentAccountGroupOptions = [
  { value: "DEBCON", label: "Debtors (Customers)" },
  { value: "CRECON", label: "Creditors (Vendors)" },
];

function getDescendantLeafAccounts(accounts, parentId) {
  const childrenByParent = new Map();
  for (const account of accounts) {
    if (!childrenByParent.has(account.parentID)) {
      childrenByParent.set(account.parentID, []);
    }
    childrenByParent.get(account.parentID).push(account.ps_Id);
  }

  if (!parentId) {
    return accounts.filter((account) => !childrenByParent.has(account.ps_Id));
  }

  const descendants = new Set();
  const pendingParentIds = [parentId];

  while (pendingParentIds.length > 0) {
    const currentParentId = pendingParentIds.pop();
    const childIds = childrenByParent.get(currentParentId) || [];

    for (const childId of childIds) {
      if (!descendants.has(childId)) {
        descendants.add(childId);
        pendingParentIds.push(childId);
      }
    }
  }

  return accounts.filter(
    (account) =>
      descendants.has(account.ps_Id) && !childrenByParent.has(account.ps_Id)
  );
}

function toSelectOption(account) {
  return {
    value: account.ps_Id,
    label: account.name,
    ...account,
  };
}

function resolveLedgerFormFromAccount(accounts, targetId, isLeaf) {
  const byId = new Map(accounts.map((account) => [account.ps_Id, account]));
  const target = byId.get(targetId);
  if (!target) return null;

  const chain = [];
  let current = target;
  while (current) {
    chain.unshift(current);
    current = current.parentID ? byId.get(current.parentID) : null;
  }

  const topGroup = chain.find((account) => Number(account.grplevel) === 2);
  if (!topGroup) return null;

  if (isLeaf) {
    const parentGroup = target.parentID ? byId.get(target.parentID) : null;
    return {
      accountGroup: toSelectOption(topGroup),
      parentGroup:
        parentGroup && parentGroup.ps_Id !== topGroup.ps_Id
          ? toSelectOption(parentGroup)
          : null,
      accounts: [toSelectOption(target)],
    };
  }

  return {
    accountGroup: toSelectOption(topGroup),
    parentGroup: toSelectOption(target),
    accounts: [],
  };
}

const Ledger = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agttp } = useAuth();
  const isTrader = Number(agttp) === 1;
  const { mutateAsync: generateReport, isPending: isGenerating } =
    useGenerateLedgerReport();
  const autoSubmitStarted = useRef(false);
  const [isAutoRunning, setIsAutoRunning] = useState(
    () => searchParams.get("auto") === "1"
  );
  const shouldAutoSubmit = searchParams.get("auto") === "1";
  const autoAccount = searchParams.get("account");
  const autoAccgrp = searchParams.get("accgrp");
  const autoFromDate = searchParams.get("fromdate") || "";
  const autoToDate = searchParams.get("todate") || "";

  const [autoSubmitError, setAutoSubmitError] = useState(null);

  const useTraderAccounts = isTrader || shouldAutoSubmit;

  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    accountGroup: null,
    parentGroup: null,
    accounts: [],
    otherAccount: null,
  });

  const { data: accountsData, isLoading: isLoadingAccounts } =
    useAllPartySupMasts(
      useTraderAccounts
        ? {
            ag_status: "1",
            noPagination: true,
          }
        : {
            parentID: formData.accountGroup?.value,
            grplevel: "0",
            level: "0",
            ag_status: "1",
            noPagination: true,
          },
      {
        enabled: useTraderAccounts || !!formData.accountGroup?.value,
      }
    );

  const accountGroupOptions = agentAccountGroupOptions;

  const accountOptions = useMemo(() => {
    const accounts = accountsData?.data || [];

    if (!isTrader) {
      return accounts
        .filter(
          (acc) => !acc.ps_Id.startsWith("BANK") && acc.ps_Id !== "CASHAC"
        )
        .map((acc) => ({
          value: acc.ps_Id,
          label: acc.name,
          ...acc,
        }));
    }

    return getDescendantLeafAccounts(accounts).map((acc) => ({
      value: acc.ps_Id,
      label: acc.name,
      ...acc,
    }));
  }, [accountsData, isTrader]);

  const showVendor = !isTrader && formData.accountGroup?.value === "CRECON";
  const showCustomer = !isTrader && formData.accountGroup?.value === "DEBCON";

  const submitLedgerReport = useCallback(
    async (data) => {
      if (!isTrader && !data.accountGroup) {
        return;
      }

      if (isTrader && data.accounts.length === 0) {
        return;
      }

      const reportData = {
        fromdate: data.fromDate,
        todate: data.toDate,
        select_grp: isTrader ? "" : data.accountGroup?.value,
        select_accgrp: isTrader
          ? ""
          : data.parentGroup?.value || data.accountGroup?.value,
        select_acc:
          data.accounts.length > 0
            ? data.accounts.map((acc) => acc.value || acc.ps_Id)
            : undefined,
        select_otheracc:
          data.otherAccount?.value || data.otherAccount?.ps_Id || "",
      };

      const result = await generateReport(reportData);
      const { htmlUrl, pdfUrl } = getDocumentUrls(result);
      const accountLabel =
        data.accounts[0]?.label ||
        data.parentGroup?.label ||
        data.accountGroup?.label ||
        "Ledger";

      const route = getDocumentViewerRoute("ledger", {
        htmlUrl,
        pdfUrl,
        documentNumber: accountLabel,
        documentType: "General Ledger",
      });

      navigate(route, { replace: shouldAutoSubmit });
    },
    [generateReport, isTrader, navigate, shouldAutoSubmit]
  );

  useEffect(() => {
    if (!shouldAutoSubmit || autoSubmitStarted.current) {
      return;
    }

    if (!isTrader) {
      if (agttp !== null && agttp !== undefined) {
        setAutoSubmitError("Ledger auto-open is only available for trader accounts.");
        setIsAutoRunning(false);
      }
      return;
    }

    if (isLoadingAccounts || !accountsData?.data?.length) {
      return;
    }

    const targetId = autoAccount || autoAccgrp;
    if (!targetId) {
      setAutoSubmitError("Missing account in URL.");
      setIsAutoRunning(false);
      return;
    }

    const resolved = resolveLedgerFormFromAccount(
      accountsData.data,
      targetId,
      !!autoAccount
    );

    if (!resolved) {
      setAutoSubmitError(`Could not resolve account "${targetId}" in chart of accounts.`);
      setIsAutoRunning(false);
      return;
    }

    autoSubmitStarted.current = true;

    const nextFormData = {
      fromDate: autoFromDate,
      toDate: autoToDate,
      accountGroup: resolved.accountGroup,
      parentGroup: resolved.parentGroup,
      accounts: resolved.accounts,
      otherAccount: null,
    };

    setFormData(nextFormData);

    submitLedgerReport(nextFormData).catch((error) => {
      autoSubmitStarted.current = false;
      setIsAutoRunning(false);
      setAutoSubmitError(
        error?.response?.data?.msg || "Failed to generate ledger report."
      );
      console.error("Error auto-generating ledger report:", error);
    });
  }, [
    shouldAutoSubmit,
    autoAccount,
    autoAccgrp,
    autoFromDate,
    autoToDate,
    isTrader,
    agttp,
    isLoadingAccounts,
    accountsData,
    submitLedgerReport,
  ]);

  const handleInputChange = (name, value) => {
    if (name === "accountGroup") {
      setFormData((prev) => ({
        ...prev,
        accountGroup: value,
        parentGroup: null,
        accounts: [],
        otherAccount: null,
      }));
      return;
    }

    if (name === "parentGroup") {
      setFormData((prev) => ({
        ...prev,
        parentGroup: value,
        accounts: [],
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isTrader && !formData.accountGroup) {
      alert("Please select Group");
      return;
    }

    if (isTrader && formData.accounts.length === 0) {
      alert("Please select at least one account");
      return;
    }

    try {
      await submitLedgerReport(formData);
    } catch (error) {
      console.error("Error generating ledger report:", error);
    }
  };

  if (shouldAutoSubmit && autoSubmitError) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] px-4">
        <div className="text-center max-w-md">
          <p className="text-red-600 font-medium">{autoSubmitError}</p>
          <button
            type="button"
            onClick={() => {
              setIsAutoRunning(false);
              setAutoSubmitError(null);
            }}
            className="mt-4 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
          >
            Open ledger form manually
          </button>
        </div>
      </div>
    );
  }

  if (shouldAutoSubmit && isAutoRunning) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600 font-medium">Generating ledger...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">
            General Ledger Report
          </h1>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleSubmit} className="px-4 py-6 sm:p-8">
          <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                label="From Date *"
                type="date"
                value={formData.fromDate}
                onChange={(e) => handleInputChange("fromDate", e.target.value)}
                fys={true}
                withinFY={true}
                required
              />
            </div>

            <div className="sm:col-span-3">
              <Input
                label="To Date *"
                type="date"
                value={formData.toDate}
                onChange={(e) => handleInputChange("toDate", e.target.value)}
                now={true}
                withinFY={true}
                required
              />
            </div>

            {!isTrader && <div className="sm:col-span-3">
              <Select
                label="Group-Account *"
                options={accountGroupOptions}
                value={formData.accountGroup}
                onChange={(value) => handleInputChange("accountGroup", value)}
                placeholder="Choose here"
                isClearable={false}
              />
            </div>}

            {!isTrader && <div className="sm:col-span-3"></div>}

            {(isTrader || formData.accountGroup) && (
              <div className="sm:col-span-3">
                <Select
                  label={isTrader ? "Accounts *" : "Account"}
                  value={formData.accounts}
                  onChange={(value) => handleInputChange("accounts", value)}
                  options={accountOptions}
                  placeholder={isTrader ? "Search accounts" : "All accounts in selected group"}
                  isMulti={true}
                  isClearable={true}
                  isSearchable={true}
                  isLoading={isLoadingAccounts}
                />
              </div>
            )}

            {showVendor && (
              <div className="sm:col-span-3">
                <SelectVendors
                  label="Vendor"
                  value={formData.otherAccount}
                  onChange={(value) => handleInputChange("otherAccount", value)}
                  isClearable={true}
                />
              </div>
            )}

            {showCustomer && (
              <div className="sm:col-span-3">
                <SelectCustomers
                  label="Customer"
                  value={formData.otherAccount}
                  onChange={(value) => handleInputChange("otherAccount", value)}
                  isClearable={true}
                />
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="submit"
              disabled={isGenerating}
              className="rounded-md bg-indigo-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? "Generating..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Ledger;
